import React, { useState, useEffect } from 'react';
import { chatAPI, ConstitutionResponse, UserMetrics, SessionMetrics, ChatHistory, ConstitutionHistory, CompanyResponse, CompanyCreateRequest, CompanyUpdateRequest } from '../services/apiService';

interface AdminModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const AdminModal: React.FC<AdminModalProps> = ({ isOpen, onClose }) => {
  const [activeTab, setActiveTab] = useState<'constitution' | 'users' | 'companies'>('constitution');
  const [constitution, setConstitution] = useState<ConstitutionResponse | null>(null);
  const [constitutionText, setConstitutionText] = useState('');
  const [constitutionHistory, setConstitutionHistory] = useState<ConstitutionHistory[]>([]);
  const [showHistory, setShowHistory] = useState(false);
  const [users, setUsers] = useState<UserMetrics[]>([]);
  const [selectedUser, setSelectedUser] = useState<UserMetrics | null>(null);
  const [userSessions, setUserSessions] = useState<SessionMetrics[]>([]);
  const [selectedSession, setSelectedSession] = useState<ChatHistory | null>(null);
  const [companies, setCompanies] = useState<CompanyResponse[]>([]);
  const [selectedCompany, setSelectedCompany] = useState<CompanyResponse | null>(null);
  const [showCreateCompany, setShowCreateCompany] = useState(false);
  const [showEditCompany, setShowEditCompany] = useState(false);
  const [newCompany, setNewCompany] = useState<CompanyCreateRequest>({
    name: '',
    mongo_collection_name: '',
    pinecone_collection_name: '',
    color_scheme: 'violet'
  });
  const [editCompany, setEditCompany] = useState<CompanyUpdateRequest>({});
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [uploadCompany, setUploadCompany] = useState<CompanyResponse | null>(null);
  const [selectedFiles, setSelectedFiles] = useState<FileList | null>(null);
  const [uploading, setUploading] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  useEffect(() => {
    if (isOpen) {
      loadInitialData();
    }
  }, [isOpen]);

  const loadInitialData = async () => {
    setLoading(true);
    setError(null);
    try {
      if (activeTab === 'constitution') {
        const constitutionData = await chatAPI.getConstitution();
        setConstitution(constitutionData);
        setConstitutionText(constitutionData.constitution);
      } else if (activeTab === 'users') {
        const usersData = await chatAPI.getAllUsersMetrics();
        setUsers(usersData);
      } else if (activeTab === 'companies') {
        const companiesData = await chatAPI.getAllCompanies();
        setCompanies(companiesData);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handleTabChange = (tab: 'constitution' | 'users' | 'companies') => {
    setActiveTab(tab);
    setError(null);
    setSuccess(null);
    setSelectedUser(null);
    setSelectedSession(null);
    setSelectedCompany(null);
    setShowCreateCompany(false);
    setShowEditCompany(false);
    loadInitialData();
  };

  const handleSaveConstitution = async () => {
    setLoading(true);
    setError(null);
    setSuccess(null);
    try {
      const result = await chatAPI.updateConstitution(constitutionText);
      setSuccess(`Constitution updated successfully by ${result.updated_by}`);
      // Auto-hide success message after 3 seconds
      setTimeout(() => setSuccess(null), 3000);
      // Reload constitution data
      const constitutionData = await chatAPI.getConstitution();
      setConstitution(constitutionData);
      // Reload history if it's being shown
      if (showHistory) {
        loadConstitutionHistory();
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update constitution');
    } finally {
      setLoading(false);
    }
  };

  const loadConstitutionHistory = async () => {
    try {
      const history = await chatAPI.getConstitutionHistory();
      setConstitutionHistory(history);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load constitution history');
    }
  };

  const handleShowHistory = async () => {
    if (!showHistory) {
      await loadConstitutionHistory();
    }
    setShowHistory(!showHistory);
  };

  const handleRestoreConstitution = (item: ConstitutionHistory) => {
    // Just paste the content to the textbox instead of immediately saving
    setConstitutionText(item.constitution);
    setSuccess(`Constitution from ${formatDate(item.created_at)} loaded into editor. Click Save to apply.`);
    // Auto-hide success message after 3 seconds
    setTimeout(() => setSuccess(null), 3000);
  };

  const handleUserClick = async (user: UserMetrics) => {
    setSelectedUser(user);
    setSelectedSession(null);
    // setLoading(true);
    setError(null);
    try {
      const sessions = await chatAPI.getUserSessions(user.id);
      setUserSessions(sessions);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load user sessions');
    } finally {
      setLoading(false);
    }
  };

  const handleSessionClick = async (session: SessionMetrics) => {
    setLoading(true);
    setError(null);
    try {
      const sessionDetails = await chatAPI.getSessionDetails(session.id);
      setSelectedSession(sessionDetails);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load session details');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="theme-bg rounded-lg shadow-xl w-full max-w-[1400px] h-full flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 theme-border">
          <h2 className="text-2xl font-bold theme-text">Admin Panel</h2>
          <button
            onClick={onClose}
            className="theme-text-muted hover:theme-text transition-colors"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Tabs */}
        <div className="flex px-5">
          <button
            onClick={() => handleTabChange('constitution')}
            className={`px-3 py-1 font-medium transition-colors rounded-lg  ${
              activeTab === 'constitution'
                ? ' border-b-2 theme-primary text-white'
                : 'theme-text-secondary hover:theme-text'
            }`}
          >
            Constitution
          </button>
          <button
            onClick={() => handleTabChange('users')}
             className={`px-3 py-1 font-medium transition-colors rounded-lg  ${
              activeTab === 'users'
                ? ' border-b-2 theme-primary text-white'
                : 'theme-text-secondary hover:theme-text'
            }`}
          >
            Users & Sessions
          </button>
          <button
            onClick={() => handleTabChange('companies')}
             className={`px-3 py-1 font-medium transition-colors rounded-lg  ${
              activeTab === 'companies'
                ? ' border-b-2 theme-primary text-white'
                : 'theme-text-secondary hover:theme-text'
            }`}
          >
            Companies
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-hidden">
          {error && (
            <div className="mx-6 mt-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded">
              {error}
            </div>
          )}


          {activeTab === 'constitution' && (
            <div className="p-6 h-full flex flex-col">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold theme-text">System Constitution</h3>
                {constitution && (
                  <div className="flex items-center space-x-3">
                    <div className="text-sm theme-text-secondary">
                      Last updated: {formatDate(constitution.last_updated)} by {constitution.updated_by}
                    </div>
                    <button
                      onClick={handleShowHistory}
                      className="px-3 py-1 text-sm bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg transition-colors"
                    >
                      {showHistory ? 'Hide History' : 'View History'}
                    </button>
                  </div>
                )}
              </div>
              
              <div className="flex-1 flex overflow-hidden">
                <div className="flex-1 flex flex-col min-h-0">
                  <textarea
                    value={constitutionText}
                    onChange={(e) => setConstitutionText(e.target.value)}
                    className="flex-1 w-full p-4 border theme-border rounded-lg theme-bg-secondary theme-text font-mono text-sm resize-none min-h-0"
                    placeholder="Enter the system constitution/prompt..."
                  />
                  
                  <div className="flex justify-end mt-4 flex-shrink-0">
                    <button
                      onClick={handleSaveConstitution}
                      disabled={loading || constitutionText === constitution?.constitution}
                      className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    >
                      {loading ? 'Saving...' : 'Save Constitution'}
                    </button>
                  </div>
                </div>

                {/* History Panel */}
                {showHistory && (
                  <div className="w-80 ml-4 border-l theme-border pl-4 flex flex-col">
                    <h4 className="text-md font-semibold theme-text mb-3">Constitution History</h4>
                    <div className="space-y-2 overflow-y-auto flex-1">
                      {constitutionHistory.map((item) => (
                        <div
                          key={item.id}
                          className="p-3 theme-bg-secondary rounded-lg"
                        >
                          <div className="flex items-center justify-between mb-2">
                            <div className="flex items-center space-x-2">
                              {item.is_current && (
                                <span className="px-2 py-1 text-xs bg-green-100 text-green-800 rounded">
                                  Current
                                </span>
                              )}
                              <span className="text-sm font-medium theme-text">
                                {item.updated_by}
                              </span>
                            </div>
                            {!item.is_current && (
                              <button
                                onClick={() => handleRestoreConstitution(item)}
                                disabled={loading}
                                className="px-2 py-1 text-xs bg-blue-100 hover:bg-blue-200 text-blue-800 rounded transition-colors disabled:opacity-50"
                              >
                                Load
                              </button>
                            )}
                          </div>
                          <div className="text-xs theme-text-secondary mb-2">
                            {formatDate(item.created_at)}
                          </div>
                          <div className="text-sm theme-text-secondary">
                            {item.constitution.length > 100 
                              ? `${item.constitution.substring(0, 100)}...`
                              : item.constitution
                            }
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {activeTab === 'users' && (
            <div className="p-6 h-full flex">
              {/* Users List */}
              <div className="w-1/4 pr-4 border-r theme-border">
                <h3 className="text-lg font-semibold theme-text mb-4">Users</h3>
                <div className="space-y-2 overflow-y-auto max-h-full">
                  {users.map((user) => (
                    <div
                      key={user.id}
                      onClick={() => handleUserClick(user)}
                      className={`p-3 rounded-lg cursor-pointer transition-colors ${
                        selectedUser?.id === user.id
                          ? 'theme-bg-secondary '
                          : 'theme-bg-accent hover:theme-bg-accent'
                      }`}
                    >
                      <div className="font-medium theme-text">
                        {user.username}
                        {user.is_admin && (
                          <span className="ml-2 px-2 py-1 text-xs bg-blue-100 text-blue-800 rounded">
                            Admin
                          </span>
                        )}
                      </div>
                      <div className="text-sm theme-text-secondary">
                        {user.total_sessions} sessions, {user.total_messages} messages
                      </div>
                      <div className="text-xs theme-text-secondary">
                        Joined: {formatDate(user.created_at)}
                      </div>
                      {user.last_activity && (
                        <div className="text-xs theme-text-secondary">
                          Last active: {formatDate(user.last_activity)}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>

              {/* Sessions List */}
              <div className="flex-1 px-4 border-r theme-border">
                <h3 className="text-lg font-semibold theme-text mb-4">
                  {selectedUser ? `${selectedUser.username}'s Sessions` : 'Select a user'}
                </h3>
                {selectedUser && (
                  <div className="space-y-2 overflow-y-auto max-h-full">
                    {userSessions.map((session) => (
                      <div
                        key={session.id}
                        onClick={() => handleSessionClick(session)}
                        className={`p-1 px-2 rounded-lg cursor-pointer transition-colors ${
                          selectedSession?.session.id === session.id
                            ? 'theme-bg-secondary '
                            : 'theme-bg-accent hover:theme-bg-accent'
                        }`}
                      >
                        <div className="font-medium theme-text truncate">
                          {session.title}
                        </div>
                        <div className="text-sm theme-text-secondary">
                          {session.message_count} messages
                        </div>
                        <div className="text-xs theme-text-secondary">
                          {formatDate(session.created_at)}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Session Details */}
              <div className="flex-1 pl-4">
                <h3 className="text-lg font-semibold theme-text mb-4">
                  {selectedSession ? 'Session Details' : 'Select a session'}
                </h3>
                {selectedSession && (
                  <div className="overflow-y-auto max-h-full">
                    <div className="mb-4 p-3 theme-bg-secondary rounded-lg">
                      <div className="font-medium theme-text">
                        {selectedSession.session.title}
                      </div>
                      <div className="text-sm theme-text-secondary">
                        Created: {formatDate(selectedSession.session.created_at)}
                      </div>
                      <div className="text-sm theme-text-secondary">
                        Updated: {formatDate(selectedSession.session.updated_at)}
                      </div>
                    </div>
                    
                    <div className="space-y-3">
                      {selectedSession.messages.map((message) => (
                        <div key={message.id} className="p-2 theme-bg-secondary rounded-lg">
                          <div className="flex items-center justify-between mb-2">
                            <span className={`text-sm font-medium ${
                              message.role === 'user' ? 'text-blue-600' : 'text-green-600'
                            }`}>
                              {message.role === 'user' ? 'User' : 'Assistant'}
                            </span>
                            <span className="text-xs theme-text-secondary">
                              {formatDate(message.created_at)}
                            </span>
                          </div>
                          <div className="text-sm theme-text whitespace-pre-wrap">
                            {message.content.length > 200 
                              ? `${message.content.substring(0, 200)}...`
                              : message.content
                            }
                          </div>
                          {/* {message.documents && message.documents.length > 0 && (
                            <div className="mt-2 text-xs theme-text-secondary">
                              {message.documents.length} document(s) referenced
                            </div>
                          )} */}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {activeTab === 'companies' && (
            <div className="p-6 h-full flex flex-col">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold theme-text">Company Management</h3>
                <button
                  onClick={() => setShowCreateCompany(true)}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                >
                  Create Company
                </button>
              </div>

              {/* Companies List */}
              <div className="flex-1 overflow-hidden">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 overflow-y-auto max-h-full">
                  {companies.map((company) => (
                    <div
                      key={company.id}
                      className="p-4 theme-bg-secondary rounded-lg border theme-border"
                    >
                      <div className="flex items-center justify-between mb-3">
                        <h4 className="font-semibold theme-text">{company.name}</h4>
                        <div className="flex flex-wrap gap-1">
                          <button
                            onClick={() => {
                              setUploadCompany(company);
                              setShowUploadModal(true);
                            }}
                            className="px-2 py-1 text-xs bg-green-100 hover:bg-green-200 text-green-800 rounded transition-colors"
                          >
                            Upload
                          </button>
                          <button
                            onClick={() => {
                              setSelectedCompany(company);
                              setEditCompany({
                                name: company.name,
                                mongo_collection_name: company.mongo_collection_name,
                                pinecone_collection_name: company.pinecone_collection_name,
                                color_scheme: company.color_scheme
                              });
                              setShowEditCompany(true);
                            }}
                            className="px-2 py-1 text-xs bg-blue-100 hover:bg-blue-200 text-blue-800 rounded transition-colors"
                          >
                            Edit
                          </button>
                          <button
                            onClick={async () => {
                              if (company.user_count > 0) {
                                setError(`Cannot delete company with ${company.user_count} users. Please reassign users first.`);
                                setTimeout(() => setError(null), 5000);
                                return;
                              }
                              if (window.confirm(`Are you sure you want to delete "${company.name}"?`)) {
                                try {
                                  await chatAPI.deleteCompany(company.id);
                                  setSuccess('Company deleted successfully');
                                  setTimeout(() => setSuccess(null), 3000);
                                  loadInitialData();
                                } catch (err) {
                                  setError(err instanceof Error ? err.message : 'Failed to delete company');
                                }
                              }
                            }}
                            disabled={company.user_count > 0}
                            className="px-2 py-1 text-xs bg-red-100 hover:bg-red-200 text-red-800 rounded transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            Delete
                          </button>
                        </div>
                      </div>
                      
                      <div className="space-y-2 text-sm">
                        <div>
                          <span className="theme-text-secondary">MongoDB Collection:</span>
                          <div className="font-mono text-xs theme-text bg-gray-100 px-2 py-1 rounded mt-1">
                            {company.mongo_collection_name}
                          </div>
                        </div>
                        <div>
                          <span className="theme-text-secondary">Pinecone Index:</span>
                          <div className="font-mono text-xs theme-text bg-gray-100 px-2 py-1 rounded mt-1">
                            {company.pinecone_collection_name}
                          </div>
                        </div>
                        <div className="flex items-center justify-between">
                          <span className="theme-text-secondary">Color Scheme:</span>
                          <span className={`px-2 py-1 text-xs rounded bg-${company.color_scheme}-100 text-${company.color_scheme}-800`}>
                            {company.color_scheme}
                          </span>
                        </div>
                        <div className="flex items-center justify-between">
                          <span className="theme-text-secondary">Users:</span>
                          <span className="font-medium theme-text">{company.user_count}</span>
                        </div>
                        <div className="text-xs theme-text-secondary">
                          Created: {formatDate(company.created_at)}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Create Company Modal */}
              {showCreateCompany && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-60">
                  <div className="theme-bg rounded-lg shadow-xl w-full max-w-md p-6">
                    <h3 className="text-lg font-semibold theme-text mb-4">Create New Company</h3>
                    
                    <div className="space-y-4">
                      <div>
                        <label className="block text-sm font-medium theme-text mb-1">Company Name</label>
                        <input
                          type="text"
                          value={newCompany.name}
                          onChange={(e) => setNewCompany({...newCompany, name: e.target.value})}
                          className="w-full p-2 border theme-border rounded-lg theme-bg-secondary theme-text"
                          placeholder="Enter company name"
                        />
                      </div>
                      
                      <div>
                        <label className="block text-sm font-medium theme-text mb-1">MongoDB Collection</label>
                        <input
                          type="text"
                          value={newCompany.mongo_collection_name}
                          onChange={(e) => setNewCompany({...newCompany, mongo_collection_name: e.target.value})}
                          className="w-full p-2 border theme-border rounded-lg theme-bg-secondary theme-text font-mono text-sm"
                          placeholder="company_documents"
                        />
                      </div>
                      
                      <div>
                        <label className="block text-sm font-medium theme-text mb-1">Pinecone Index</label>
                        <input
                          type="text"
                          value={newCompany.pinecone_collection_name}
                          onChange={(e) => setNewCompany({...newCompany, pinecone_collection_name: e.target.value})}
                          className="w-full p-2 border theme-border rounded-lg theme-bg-secondary theme-text font-mono text-sm"
                          placeholder="company-index"
                        />
                      </div>
                      
                      <div>
                        <label className="block text-sm font-medium theme-text mb-1">Color Scheme</label>
                        <select
                          value={newCompany.color_scheme}
                          onChange={(e) => setNewCompany({...newCompany, color_scheme: e.target.value})}
                          className="w-full p-2 border theme-border rounded-lg theme-bg-secondary theme-text"
                        >
                          <option value="violet">Violet</option>
                          <option value="blue">Blue</option>
                          <option value="green">Green</option>
                          <option value="red">Red</option>
                          <option value="orange">Orange</option>
                          <option value="purple">Purple</option>
                          <option value="pink">Pink</option>
                          <option value="indigo">Indigo</option>
                        </select>
                      </div>
                    </div>
                    
                    <div className="flex justify-end space-x-3 mt-6">
                      <button
                        onClick={() => {
                          setShowCreateCompany(false);
                          setNewCompany({
                            name: '',
                            mongo_collection_name: '',
                            pinecone_collection_name: '',
                            color_scheme: 'violet'
                          });
                        }}
                        className="px-4 py-2 text-gray-600 hover:text-gray-800 transition-colors"
                      >
                        Cancel
                      </button>
                      <button
                        onClick={async () => {
                          try {
                            await chatAPI.createCompany(newCompany);
                            setSuccess('Company created successfully');
                            setTimeout(() => setSuccess(null), 3000);
                            setShowCreateCompany(false);
                            setNewCompany({
                              name: '',
                              mongo_collection_name: '',
                              pinecone_collection_name: '',
                              color_scheme: 'violet'
                            });
                            loadInitialData();
                          } catch (err) {
                            setError(err instanceof Error ? err.message : 'Failed to create company');
                          }
                        }}
                        disabled={!newCompany.name || !newCompany.mongo_collection_name || !newCompany.pinecone_collection_name}
                        className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                      >
                        Create Company
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {/* Edit Company Modal */}
              {showEditCompany && selectedCompany && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-60">
                  <div className="theme-bg rounded-lg shadow-xl w-full max-w-md p-6">
                    <h3 className="text-lg font-semibold theme-text mb-4">Edit Company</h3>
                    
                    <div className="space-y-4">
                      <div>
                        <label className="block text-sm font-medium theme-text mb-1">Company Name</label>
                        <input
                          type="text"
                          value={editCompany.name || ''}
                          onChange={(e) => setEditCompany({...editCompany, name: e.target.value})}
                          className="w-full p-2 border theme-border rounded-lg theme-bg-secondary theme-text"
                          placeholder="Enter company name"
                        />
                      </div>
                      
                      <div>
                        <label className="block text-sm font-medium theme-text mb-1">MongoDB Collection</label>
                        <input
                          type="text"
                          value={editCompany.mongo_collection_name || ''}
                          onChange={(e) => setEditCompany({...editCompany, mongo_collection_name: e.target.value})}
                          className="w-full p-2 border theme-border rounded-lg theme-bg-secondary theme-text font-mono text-sm"
                          placeholder="company_documents"
                        />
                      </div>
                      
                      <div>
                        <label className="block text-sm font-medium theme-text mb-1">Pinecone Index</label>
                        <input
                          type="text"
                          value={editCompany.pinecone_collection_name || ''}
                          onChange={(e) => setEditCompany({...editCompany, pinecone_collection_name: e.target.value})}
                          className="w-full p-2 border theme-border rounded-lg theme-bg-secondary theme-text font-mono text-sm"
                          placeholder="company-index"
                        />
                      </div>
                      
                      <div>
                        <label className="block text-sm font-medium theme-text mb-1">Color Scheme</label>
                        <select
                          value={editCompany.color_scheme || 'violet'}
                          onChange={(e) => setEditCompany({...editCompany, color_scheme: e.target.value})}
                          className="w-full p-2 border theme-border rounded-lg theme-bg-secondary theme-text"
                        >
                          <option value="violet">Violet</option>
                          <option value="blue">Blue</option>
                          <option value="green">Green</option>
                          <option value="red">Red</option>
                          <option value="orange">Orange</option>
                          <option value="purple">Purple</option>
                          <option value="pink">Pink</option>
                          <option value="indigo">Indigo</option>
                        </select>
                      </div>
                    </div>
                    
                    <div className="flex justify-end space-x-3 mt-6">
                      <button
                        onClick={() => {
                          setShowEditCompany(false);
                          setSelectedCompany(null);
                          setEditCompany({});
                        }}
                        className="px-4 py-2 text-gray-600 hover:text-gray-800 transition-colors"
                      >
                        Cancel
                      </button>
                      <button
                        onClick={async () => {
                          try {
                            await chatAPI.updateCompany(selectedCompany.id, editCompany);
                            setSuccess('Company updated successfully');
                            setTimeout(() => setSuccess(null), 3000);
                            setShowEditCompany(false);
                            setSelectedCompany(null);
                            setEditCompany({});
                            loadInitialData();
                          } catch (err) {
                            setError(err instanceof Error ? err.message : 'Failed to update company');
                          }
                        }}
                        className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                      >
                        Update Company
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {/* Upload Documents Modal */}
              {showUploadModal && uploadCompany && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-60">
                  <div className="theme-bg rounded-lg shadow-xl w-full max-w-lg p-6">
                    <h3 className="text-lg font-semibold theme-text mb-4">
                      Upload Documents - {uploadCompany.name}
                    </h3>
                    
                    <div className="space-y-4">
                      <div className="p-4 bg-blue-50 rounded-lg">
                        <div className="text-sm text-blue-800">
                          <div><strong>MongoDB Collection:</strong> {uploadCompany.mongo_collection_name}</div>
                          <div><strong>Pinecone Index:</strong> {uploadCompany.pinecone_collection_name}</div>
                        </div>
                      </div>
                      
                      <div>
                        <label className="block text-sm font-medium theme-text mb-2">
                          Select PDF Documents
                        </label>
                        <input
                          type="file"
                          multiple
                          accept=".pdf"
                          onChange={(e) => setSelectedFiles(e.target.files)}
                          className="w-full p-2 border theme-border rounded-lg theme-bg-secondary theme-text file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
                        />
                        <div className="text-xs theme-text-secondary mt-1">
                          Only PDF files are supported. Multiple files can be selected.
                        </div>
                      </div>
                      
                      {selectedFiles && selectedFiles.length > 0 && (
                        <div className="p-3 bg-gray-50 rounded-lg">
                          <div className="text-sm font-medium theme-text mb-2">
                            Selected Files ({selectedFiles.length}):
                          </div>
                          <div className="space-y-1 max-h-32 overflow-y-auto">
                            {Array.from(selectedFiles).map((file, index) => (
                              <div key={index} className="text-xs theme-text-secondary flex items-center justify-between">
                                <span className="truncate">{file.name}</span>
                                <span className="ml-2 text-gray-500">
                                  {(file.size / 1024 / 1024).toFixed(1)} MB
                                </span>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                      
                      {uploading && (
                        <div className="p-3 bg-blue-50 rounded-lg">
                          <div className="flex items-center space-x-2">
                            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
                            <span className="text-sm text-blue-800">
                              Processing documents... This may take a few minutes.
                            </span>
                          </div>
                        </div>
                      )}
                    </div>
                    
                    <div className="flex justify-end space-x-3 mt-6">
                      <button
                        onClick={() => {
                          setShowUploadModal(false);
                          setUploadCompany(null);
                          setSelectedFiles(null);
                        }}
                        disabled={uploading}
                        className="px-4 py-2 text-gray-600 hover:text-gray-800 transition-colors disabled:opacity-50"
                      >
                        Cancel
                      </button>
                      <button
                        onClick={async () => {
                          if (!selectedFiles || selectedFiles.length === 0) {
                            setError('Please select at least one PDF file');
                            return;
                          }
                          
                          setUploading(true);
                          setError(null);
                          
                          try {
                            const result = await chatAPI.uploadDocuments(uploadCompany.id, selectedFiles);
                            
                            let successMessage = `Successfully uploaded ${result.uploaded_files.length} files to ${result.company_name}`;
                            if (result.statistics?.documents_processed) {
                              successMessage += `. Processed ${result.statistics.documents_processed} documents.`;
                            }
                            
                            setSuccess(successMessage);
                            setTimeout(() => setSuccess(null), 5000);
                            
                            setShowUploadModal(false);
                            setUploadCompany(null);
                            setSelectedFiles(null);
                            
                          } catch (err) {
                            setError(err instanceof Error ? err.message : 'Failed to upload documents');
                          } finally {
                            setUploading(false);
                          }
                        }}
                        disabled={!selectedFiles || selectedFiles.length === 0 || uploading}
                        className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                      >
                        {uploading ? 'Uploading...' : `Upload ${selectedFiles?.length || 0} Files`}
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Toast Notification */}
        {success && (
          <div className="fixed bottom-4 right-4 bg-green-500 text-white px-6 py-3 rounded-lg shadow-lg z-60 max-w-md">
            <div className="flex items-center space-x-2">
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
              <span>{success}</span>
            </div>
          </div>
        )}

        {/* {loading && (
          <div className="absolute inset-0 bg-black bg-opacity-25 flex items-center justify-center">
            <div className="bg-white p-4 rounded-lg">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            </div>
          </div>
        )} */}
      </div>
    </div>
  );
};

export default AdminModal;
